import React, { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { problems, languageConfigs } from '../lib/problems'
import { Problem, Message, CodeContext } from '../types'
import CodeEditor from './CodeEditor'
import VoiceInput from './VoiceInput'
import FeedbackPanel from './FeedbackPanel'
import Header from './Header'
import LanguageSelector from './LanguageSelector'
import TestCases from './TestCases'
import aiService from '../services/ai'
import { Headphones, MessageCircle, Volume2, VolumeX, Mic, MicOff, Bot } from 'lucide-react'

const SolveProblemPage: React.FC = () => {
  const { problemId } = useParams<{ problemId: string }>()
  const navigate = useNavigate()
  const problem = problems.find(p => p.id === problemId)

  const [messages, setMessages] = useState<Message[]>([])
  const [isListening, setIsListening] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState('python')
  const [currentCode, setCurrentCode] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [autoFeedback, setAutoFeedback] = useState(false)
  const [lastFeedbackTime, setLastFeedbackTime] = useState(0)
  const [isTyping, setIsTyping] = useState(false)
  const [conversationMode, setConversationMode] = useState(false)
  const [lastCodeLength, setLastCodeLength] = useState(0)
  const [isAISpeaking, setIsAISpeaking] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const feedbackTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const feedbackPanelRef = useRef<HTMLDivElement>(null)
  const speakingCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [chatInput, setChatInput] = useState('')

  // Disabled auto-scroll to fix the scroll bug
  // useEffect(() => {
  //   // Only auto-scroll if user is near the bottom of the messages
  //   if (messagesEndRef.current && feedbackPanelRef.current) {
  //     const panel = feedbackPanelRef.current
  //     const isNearBottom = panel.scrollHeight - panel.scrollTop - panel.clientHeight < 100
  //     
  //     if (isNearBottom) {
  //       messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
  //     }
  //   }
  // }, [messages])

  useEffect(() => {
    if (problem) {
      const defaultLanguage = problem.supportedLanguages?.[0] || 'python'
      setSelectedLanguage(defaultLanguage)
      setCurrentCode(getStarterCodeForLanguage(problem.starterCode || '', defaultLanguage))
      setMessages([])
      setLastCodeLength(0)
    }
  }, [problemId])

  // Typing detection
  useEffect(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    setIsTyping(true)
    
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false)
    }, 1500) // User stopped typing after 1.5 seconds

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [currentCode])

  // Human-like auto-feedback functionality
  useEffect(() => {
    if (!autoFeedback || !currentCode.trim() || isProcessing || isTyping) return

    // Only provide feedback if there's significant code change
    const codeChange = Math.abs(currentCode.length - lastCodeLength)
    if (codeChange < 10) return // Only feedback on meaningful changes

    // Debounce feedback to avoid too many requests
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current)
    }

    feedbackTimeoutRef.current = setTimeout(async () => {
      const now = Date.now()
      // Longer cooldown for more human-like interaction
      const minInterval = conversationMode ? 8000 : 12000 // 8-12 seconds between feedbacks
      if (now - lastFeedbackTime < minInterval) return

      setIsProcessing(true)
      try {
        const codeContext: CodeContext = {
          transcribedThoughts: '',
          currentCode: currentCode,
          filePath: `${problem?.title.replace(/\s+/g, '_').toLowerCase()}${languageConfigs[selectedLanguage as keyof typeof languageConfigs]?.extension || '.js'}`,
          language: selectedLanguage,
          problemDescription: problem?.description || '',
          conversationMode: conversationMode,
          previousMessages: messages.slice(-3) // Last 3 messages for context
        }
        
        const feedback = await aiService.getFeedback(codeContext)
        const aiMessage: Message = {
          id: Date.now().toString(),
          type: 'ai',
          content: feedback,
          timestamp: new Date(),
        }
        setMessages(prev => [...prev, aiMessage])
        setLastFeedbackTime(now)
        setLastCodeLength(currentCode.length)
        
        // Only speak if Voice Feedback is enabled
        if (autoFeedback) {
          await aiService.speakFeedback(feedback, true)
        }
      } catch (error) {
        console.error('Auto-feedback error:', error)
      } finally {
        setIsProcessing(false)
      }
    }, 3000)

    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current)
      }
    }
  }, [currentCode, autoFeedback, isProcessing, lastFeedbackTime, selectedLanguage, problem, isTyping, lastCodeLength, conversationMode, messages])

  // Check AI speaking status periodically
  useEffect(() => {
    speakingCheckIntervalRef.current = setInterval(() => {
      const speaking = aiService.isCurrentlySpeaking()
      setIsAISpeaking(speaking)
    }, 100) // Check every 100ms

    return () => {
      if (speakingCheckIntervalRef.current) {
        clearInterval(speakingCheckIntervalRef.current)
      }
    }
  }, [])

  const getStarterCodeForLanguage = (starterCode: string, language: string): string => {
    if (!starterCode) return ''
    
    const pythonMatch = starterCode.match(/# Python\n([\s\S]*?)(?=# JavaScript|$)/)
    const jsMatch = starterCode.match(/# JavaScript\n([\s\S]*?)(?=# |$)/)
    
    if (language === 'python' && pythonMatch) {
      return pythonMatch[1].trim()
    } else if (language === 'javascript' && jsMatch) {
      return jsMatch[1].trim()
    } else if (language === 'java') {
      return `public class Solution {
    public static void main(String[] args) {
        // Your code here
    }
}`
    } else if (language === 'cpp') {
      return `#include <iostream>
#include <vector>
using namespace std;

int main() {
    // Your code here
    return 0;
}`
    }
    
    return starterCode
  }

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language)
    if (problem) {
      const newStarterCode = getStarterCodeForLanguage(problem.starterCode || '', language)
      setCurrentCode(newStarterCode)
    }
  }

  if (!problem) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-4">Problem not found</h2>
        <button className="btn-primary" onClick={() => navigate('/problems')}>Back to Problems</button>
      </div>
    )
  }

  const handleVoiceInput = async (transcript: string) => {
    if (!transcript.trim()) return
    
    // Set conversation mode when user speaks
    setConversationMode(true)
    
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: transcript,
      timestamp: new Date(),
    }
    
    // Add user message to state first
    setMessages(prev => [...prev, userMessage])
    setIsProcessing(true)
    
    try {
      // Create updated messages array with the new user message
      const updatedMessages = [...messages, userMessage]
      
      const codeContext: CodeContext = {
        transcribedThoughts: transcript,
        currentCode: currentCode,
        filePath: `${problem.title.replace(/\s+/g, '_').toLowerCase()}${languageConfigs[selectedLanguage as keyof typeof languageConfigs]?.extension || '.js'}`,
        language: selectedLanguage,
        problemDescription: problem.description,
        conversationMode: true,
        previousMessages: updatedMessages.slice(-4) // Include the new user message
      }
      const feedback = await aiService.getFeedback(codeContext)
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: feedback,
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, aiMessage])
      
      // Only speak if Voice Feedback is enabled
      if (autoFeedback) {
        await aiService.speakFeedback(feedback, true)
      }
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsProcessing(false)
    }
  }

  const clearSession = () => {
    setMessages([])
    setCurrentCode(getStarterCodeForLanguage(problem.starterCode || '', selectedLanguage))
    setConversationMode(false)
    setLastCodeLength(0)
  }

  const toggleAutoFeedback = () => {
    setAutoFeedback(!autoFeedback)
    if (!autoFeedback) {
      setConversationMode(true) // Enable conversation mode when auto-feedback is turned on
    }
  }

  const toggleConversationMode = () => {
    setConversationMode(!conversationMode)
  }

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!chatInput.trim() || isProcessing) return
    await handleVoiceInput(chatInput)
    setChatInput('')
  }

  const languageConfig = languageConfigs[selectedLanguage as keyof typeof languageConfigs]

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header onClearSession={clearSession} />
      
      {/* Problem Description - Compact */}
      <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-gray-200">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{problem.title}</h2>
                <p className="text-sm text-gray-600 mt-1">{problem.description}</p>
              </div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                problem.difficulty === 'Easy' ? 'bg-green-100 text-green-700' : 
                problem.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 
                'bg-red-100 text-red-700'
              }`}>
                {problem.difficulty}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {problem.tags?.map(tag => (
                <span key={tag} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        <div className="container mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
            {/* Code Editor - Left Side */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Code Editor</h3>
                  <LanguageSelector
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={handleLanguageChange}
                    supportedLanguages={problem.supportedLanguages}
                  />
                </div>
                <div className="flex-1 min-h-0">
                  <CodeEditor
                    value={currentCode}
                    onChange={setCurrentCode}
                    language={languageConfig?.monacoLanguage || 'javascript'}
                  />
                </div>
              </div>
            </div>

            {/* AI Mentor Panel - Right Side */}
            <div className="flex flex-col space-y-6">
              {/* AI Mentor Chat */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 min-h-0 flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">AI Mentor</h3>
                      <p className="text-xs text-gray-500">Real-time coding guidance</p>
                    </div>
                  </div>
                </div>

                {/* Status Bar */}
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {isTyping && (
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                          <span>Typing...</span>
                        </div>
                      )}
                      {isProcessing && (
                        <div className="flex items-center space-x-2 text-xs text-purple-600">
                          <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse"></div>
                          <span>Thinking...</span>
                        </div>
                      )}
                      {isAISpeaking && (
                        <div className="flex items-center space-x-2 text-xs text-orange-600">
                          <Headphones className="h-3 w-3 animate-pulse" />
                          <span>Speaking...</span>
                        </div>
                      )}
                    </div>
                    {isAISpeaking && (
                      <button
                        onClick={() => aiService.stopSpeaking()}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Stop
                      </button>
                    )}
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="flex-1 min-h-0 overflow-hidden">
                  <FeedbackPanel
                    messages={messages}
                    isProcessing={isProcessing}
                    messagesEndRef={messagesEndRef}
                    feedbackPanelRef={feedbackPanelRef}
                  />
                </div>

                {/* Controls */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex space-x-2 mb-3">
                    <button
                      onClick={toggleAutoFeedback}
                      className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        autoFeedback 
                          ? 'bg-primary-100 text-primary-700 border border-primary-200 shadow-sm' 
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Volume2 className={`h-4 w-4 ${autoFeedback ? 'text-primary-600' : 'text-gray-400'}`} />
                      <span>Voice</span>
                    </button>
                    
                    <button
                      onClick={toggleConversationMode}
                      className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        conversationMode 
                          ? 'bg-primary-100 text-primary-700 border border-primary-200 shadow-sm' 
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <MessageCircle className={`h-4 w-4 ${conversationMode ? 'text-primary-600' : 'text-gray-400'}`} />
                      <span>Chat</span>
                    </button>
                  </div>

                  {/* Chat Input for Chat Mode */}
                  {conversationMode && (
                    <form onSubmit={handleChatSubmit} className="flex items-center space-x-2 mb-3">
                      <input
                        type="text"
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                        placeholder="Type your question and press Enter..."
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        disabled={isProcessing}
                        autoFocus
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                        disabled={isProcessing || !chatInput.trim()}
                      >
                        Send
                      </button>
                    </form>
                  )}

                  {/* Voice Input */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <VoiceInput
                      onTranscript={handleVoiceInput}
                      isListening={isListening}
                      setIsListening={setIsListening}
                      disabled={isProcessing}
                    />
                  </div>
                </div>
              </div>

              {/* Test Cases */}
              {problem.testCases && problem.testCases.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Test Cases</h3>
                  </div>
                  <div className="p-4">
                    <TestCases
                      testCases={problem.testCases}
                      currentCode={currentCode}
                      selectedLanguage={selectedLanguage}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SolveProblemPage 